/**
 * Tiny fetch wrapper — JWT bearer + tenant apiUrl + JSON headers.
 * Kept deliberately small so tests can stub `global.fetch` without extra plumbing.
 */

import { tenantConfig } from '../config/tenant'

const TOKEN_KEY = 'access_token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

function url(path: string): string {
  const base = tenantConfig.apiUrl.replace(/\/$/, '')
  const trimmed = path.startsWith('/') ? path : `/${path}`
  return `${base}${trimmed}`
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  }
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url(path), { ...init, headers })
  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const body = await response.json()
      if (body?.detail) detail = String(body.detail)
    } catch {
      /* ignore */
    }
    throw new ApiError(detail, response.status)
  }
  if (response.status === 204) return undefined as unknown as T
  return response.json() as Promise<T>
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

async function uploadRequest<T>(path: string, form: FormData): Promise<T> {
  // Multipart: DON'T set Content-Type — the browser adds the boundary itself.
  const headers: Record<string, string> = {}
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  const response = await fetch(url(path), { method: 'POST', body: form, headers })
  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const body = await response.json()
      if (body?.detail) detail = String(body.detail)
    } catch {
      /* ignore */
    }
    throw new ApiError(detail, response.status)
  }
  return response.json() as Promise<T>
}

async function downloadRequest(path: string): Promise<Blob> {
  // GET a file (e.g. a CSV export) as a Blob, carrying the JWT like request().
  const headers: Record<string, string> = {}
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  const response = await fetch(url(path), { method: 'GET', headers })
  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const body = await response.json()
      if (body?.detail) detail = String(body.detail)
    } catch {
      /* ignore */
    }
    throw new ApiError(detail, response.status)
  }
  return response.blob()
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  download: (path: string) => downloadRequest(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData) => uploadRequest<T>(path, form),
}
